import { useEffect, useRef, type ReactNode } from 'react'
import type { DataReferenceOffers } from '../api/client'
import { renderSample } from './DataReferencePicker'
import { mentionLevel, mentionRowKey, type Mention, type MentionRow } from './mentionAtCaret'

export interface DataMentionPopupProps {
  offers: DataReferenceOffers | null
  error: string | null
  mention: Mention
  highlight: number
  onHighlight: (index: number) => void
  onPick: (row: MentionRow) => void
}

const popupClass = 'absolute z-20 bottom-full mb-2 w-full max-w-3xl rounded border border-gray-200 bg-white shadow-lg'

export function DataMentionPopup({
  offers,
  error,
  mention,
  highlight,
  onHighlight,
  onPick,
}: DataMentionPopupProps) {
  const listRef = useRef<HTMLUListElement>(null)
  const optionRefs = useRef<Array<HTMLLIElement | null>>([])
  const rows = offers && !error && !offers.note ? mentionLevel(offers, mention) : []
  const rowsKey = rows.map(mentionRowKey).join('\u0000')

  useEffect(() => {
    const list = listRef.current
    const option = optionRefs.current[highlight]
    if (!list || !option) return

    const optionTop = option.offsetTop
    const optionBottom = optionTop + option.offsetHeight
    const visibleTop = list.scrollTop
    const visibleBottom = visibleTop + list.clientHeight
    if (optionTop < visibleTop) {
      list.scrollTop = optionTop
    } else if (optionBottom > visibleBottom) {
      list.scrollTop = optionBottom - list.clientHeight
    }
  }, [highlight, rowsKey])

  let body: ReactNode

  if (error) {
    body = (
      <p data-testid="data-mention-error" role="alert" className="p-3 text-sm text-red-700">
        Could not load data references: {error}
      </p>
    )
  } else if (!offers) {
    body = (
      <p className="p-3 text-sm text-gray-600">Loading data references…</p>
    )
  } else if (offers.note) {
    body = (
      <div data-testid="data-mention-unavailable" role="status" className="p-3 text-sm text-gray-600">
        <p>Data references are unavailable right now: {offers.note}</p>
        <p className="mt-1 text-xs">
          Nothing was found because nothing could be asked — not because this fleet has none.
        </p>
      </div>
    )
  } else if (offers.sets.length === 0) {
    body = (
      <p data-testid="data-mention-empty" role="status" className="p-3 text-sm text-gray-600">
        This fleet has no published reference data yet.
      </p>
    )
  } else {
    if (rows.length === 0) {
      const query = mention.filter || mention.segments[mention.segments.length - 1] || '@'
      body = (
        <p data-testid="data-mention-empty" role="status" className="p-3 text-sm text-gray-600">
          No published value matches {query}.
        </p>
      )
    } else {
      const activeSet = mention.segments.length === 1
        ? offers.sets.find((set) => set.set === mention.segments[0])
        : undefined

      body = (
        <>
          <ul
            ref={listRef}
            role="listbox"
            aria-label="Data references"
            className="max-h-72 overflow-y-auto py-1"
          >
            {rows.map((row, index) => (
              <li
                key={mentionRowKey(row)}
                ref={option => { optionRefs.current[index] = option }}
                data-testid="data-mention-option"
                role="option"
                aria-selected={index === highlight}
                onMouseEnter={() => onHighlight(index)}
                onClick={() => onPick(row)}
                className={`cursor-pointer px-3 py-2 text-sm ${
                  index === highlight
                    ? 'bg-indigo-50 text-indigo-700'
                    : 'text-gray-800 hover:bg-gray-50'
                }`}
              >
                {row.kind === 'level' && (
                  <span className="flex items-center gap-2">
                    <span>{row.set.set}</span>
                    {row.set.deprecated && (
                      <span className="text-xs text-gray-500">(deprecated)</span>
                    )}
                  </span>
                )}
                {row.kind === 'row' && <span>{row.key}</span>}
                {row.kind === 'field' && (
                  <span className="flex items-baseline justify-between gap-3">
                    <span>{row.field}</span>
                    <span className="text-xs text-gray-500">
                      {renderSample(row.offer.sample)}
                    </span>
                  </span>
                )}
              </li>
            ))}
          </ul>
          {activeSet?.keys_truncated && (
            <p data-testid="data-mention-truncated" role="status" className="border-t border-gray-100 px-3 py-2 text-xs text-gray-500">
              Showing the first {activeSet.key_limit} keys of more — narrow the set or pick a runtime
              operand instead of a literal key.
            </p>
          )}
        </>
      )
    }
  }

  return (
    <div id="data-mention-popup" data-testid="data-mention-popup" className={popupClass}>
      {body}
    </div>
  )
}
